/**
 * @file InvoiceTable.js
 * @version 1.15.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-09-24
 * @modified 2026-11-01
 *
 * 1.12.0 (2026-10-08): the expanded row's settlement strip also shows the
 * credits / debits raised for the invoice ("Credit CN-…", "Debit INV-…") or,
 * on one of those, the invoice it was raised for ("Credit for INV-…") —
 * `adjustments` from GET /:id/settlement (invoicing.invoice_links).
 *
 * 1.11.0 (2026-10-07): a till document whose customer was changed shows a
 * swap icon beside the customer, the changes and reasons in its tooltip.
 *
 * 1.12.1 (2026-10-20, Cam): the expanded lines show each quantity in its own
 * unit with the tonnes beneath (QtyWithTonnes).
 *
 * 1.13.0 (2026-10-21, Cam): the expanded lines drop the Item # column — the
 * item code is a badge left of the words (ItemCodeDescription) in a column
 * now called Sales description. The other columns keep their widths
 * (InvoiceTable.css, renumbered).
 *
 * 1.14.0 (2026-10-28, Cam — rep mobile review): on a rep's phone each
 * invoice is a RepDocCard instead of a table row. The card opens the invoice
 * (the row's Preview action) and has a Customer button; status, due date and
 * what is left to pay in words (utils/repDocCards). The old phone card was
 * this table squeezed by CSS: 9–10px labels and a tap that only highlighted
 * the row. The desktop table is unchanged; the two never render together.
 *
 * 1.15.0 (2026-11-01, Cam — units corrected after an invoice was paid): the
 * Status cell carries "Needs correcting" (InvoiceCorrectionBadge — each flag's
 * words in its title) while the invoice has an open correction flag.
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { API_URL } from '../config';
import './DataTable.css';
import './TableStyles.css';
import './InvoiceTable.css';
import './ResizableColumns.css';
import PaginationControls from './PaginationControls';
import { describeCustomerChanges } from '../utils/customerChange';
import LoadingSpinner from './LoadingSpinner';
import RowActionsMenu from './RowActionsMenu';
import TableContextMenu from './TableContextMenu';
import useResizableColumns from '../hooks/useResizableColumns';
import useTableContextMenu from '../hooks/useTableContextMenu';
import useColumnFilters from '../hooks/useColumnFilters';
import ColumnFilterMenu from './ColumnFilterMenu';
import ColumnFilterNote from './ColumnFilterNote';
import { formatVatWithRate } from '../utils/taxTypes';
import { formatDateShort } from '../utils/dateFormatters';
import { paymentNature } from '../utils/paymentTypes';
import { formatPrice } from '../utils/currencyFormatter';
import QtyWithTonnes from './QtyWithTonnes';
import ItemCodeDescription from './ItemCodeDescription';
import EntityLink from './EntityLink';
import { InvoiceCorrectionBadge } from './InvoiceCorrectionPanel';
import SortOptionsMenu, { useSortMenu } from './SortOptionsMenu';
import useRepMobile from '../hooks/useRepMobile';
import RepDocCard, { RepDocCards } from './RepDocCard';
import { repInvoiceCard } from '../utils/repDocCards';

function InvoiceTable({
  invoices = [],
  columns = ['all'],
  actions = [],
  sortField = 'invoice_date',
  sortDirection = 'desc',
  onSort,
  pagination,
  onPageChange,
  loading = false,
  emptyMessage = 'No invoices found',
  expandable = false,
  showSettlement = false,
  expandedRows = null,
  onExpandedRowsChange = null,
  lineItems = null,
  // Server-computed distinct values per filterable column, and where to persist
  // the selection. Absent means page-scoped filtering — see the hook.
  facets = null,
  columnFilterKey = null,
  onColumnFiltersChange = null,
  onColumnFiltersQuery = null,
  // Bumped by the parent's "Clear Filters" — the selection lives in this
  // component, so the parent needs a way to ask for it to be dropped without
  // reaching into the hook's state.
  clearColumnFiltersSignal = 0,
  // The parent's header filters (utils/activeFilters) and its Clear Filters
  // handler, so the toolbar pill names and clears those too. Absent, the pill
  // shows column filters only, cleared by the hook.
  headerFilters = null,
  onClearFilters = null,
  onLineItemsChange = null,
  loadingLineItems = null,
  onLoadingLineItemsChange = null,
  onInvoiceClick = null,
  onContactClick = null,
  toolbarLeft = null,
  selectedIds = null,
  bulkActions = null,
  genericActions = null
}) {
  // Internal state for expandable rows when not controlled externally
  const [internalExpandedRows, setInternalExpandedRows] = useState(new Set());
  const [internalLineItems, setInternalLineItems] = useState({});
  const [internalLoadingLineItems, setInternalLoadingLineItems] = useState({});
  // Per-invoice settlement (payments + credit allocations), fetched on expand
  // when showSettlement is set. Keyed by invoice id.
  const [settlement, setSettlement] = useState({});

  // Use external state if provided, otherwise use internal
  const expanded = expandedRows !== null ? expandedRows : internalExpandedRows;
  const setExpanded = onExpandedRowsChange || setInternalExpandedRows;
  const lines = lineItems !== null ? lineItems : internalLineItems;
  const setLines = onLineItemsChange || setInternalLineItems;
  const loadingLines = loadingLineItems !== null ? loadingLineItems : internalLoadingLineItems;
  const setLoadingLines = onLoadingLineItemsChange || setInternalLoadingLineItems;

  // Identity of the CURRENT result set, as a stable string.
  //
  // The `invoices` prop cannot be used as an effect dependency: callers build it
  // inline (SearchInvoices maps over its state to attach `checked`), so it is a
  // brand-new array on every parent render. Depending on it made the reset below
  // run on every render of the parent — so opening any modal over the table
  // silently wiped the payments and credit allocations out of every expanded
  // row, while the rows themselves stayed open because `expandedRows` is
  // controlled from outside. That was the "allocations vanish and I can't tell
  // when" bug: the trigger was any parent state change at all.
  const invoiceIdsKey = useMemo(
    () => (invoices || []).map((i) => i.id).join(','),
    [invoices]
  );

  // A genuinely new result set (new search, page change) invalidates cached
  // settlement — but only for invoices that have actually gone. Pruning rather
  // than clearing means a row that is still on screen keeps what it fetched.
  useEffect(() => {
    if (expandedRows === null) {
      setInternalExpandedRows(new Set());
    }
    setSettlement((prev) => {
      const present = new Set((invoices || []).map((i) => String(i.id)));
      const next = {};
      for (const key of Object.keys(prev)) {
        if (present.has(String(key))) next[key] = prev[key];
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceIdsKey]);

  // Backfill settlement for any expanded row that hasn't got it.
  //
  // Fetching only on the expand click left three ways to end up with an
  // expanded row and no allocations: Expand All (which fetches line items but
  // never called fetchSettlement), expanded state restored from the parent, and
  // the wipe described above. Driving it from the rendered state instead means
  // an open row always ends up with its data, whatever opened it.
  useEffect(() => {
    if (!showSettlement) return;
    // Restricted to invoices actually on this page: an externally-controlled
    // expanded set can still name rows from the previous page, and fetching
    // settlement for something nobody can see is pure waste.
    const present = new Set((invoices || []).map((i) => String(i.id)));
    for (const id of expanded) {
      if (present.has(String(id)) && settlement[id] === undefined) fetchSettlement(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, settlement, showSettlement, invoiceIdsKey]);

  const toggleRowExpand = async (invoiceId) => {
    const newExpanded = new Set(expanded);

    if (newExpanded.has(invoiceId)) {
      newExpanded.delete(invoiceId);
    } else {
      newExpanded.add(invoiceId);

      // Fetch line items if not already loaded
      if (!lines[invoiceId]) {
        await fetchLineItems(invoiceId);
      }
      // Settlement is handled by the backfill effect above — it fires as soon as
      // this row lands in `expanded`, and covers every other route in too.
    }

    setExpanded(newExpanded);
  };

  const fetchSettlement = async (invoiceId) => {
    // Mark as in-flight (null) so we don't refetch; replace with data or [] shape.
    setSettlement(prev => ({ ...prev, [invoiceId]: null }));
    try {
      const res = await fetch(`${API_URL}/api/invoices/${invoiceId}/settlement`, { credentials: 'include' });
      const data = await res.json();
      setSettlement(prev => ({
        ...prev,
        [invoiceId]: {
          payments: data.payments || [],
          creditAllocations: data.creditAllocations || [],
          adjustments: data.adjustments || []
        }
      }));
    } catch (error) {
      console.error('Failed to fetch settlement:', error);
      setSettlement(prev => ({ ...prev, [invoiceId]: { payments: [], creditAllocations: [], adjustments: [] } }));
    }
  };

  const fetchLineItems = async (invoiceId) => {
    setLoadingLines(prev => ({ ...prev, [invoiceId]: true }));
    try {
      // First, ensure line items exist in DB (lazy-load from Xero if needed)
      await fetch(
        `${API_URL}/api/invoices/${invoiceId}/ensure-line-items`,
        { credentials: 'include' }
      );

      // Then fetch the line items for display
      const response = await fetch(
        `${API_URL}/api/invoices/${invoiceId}/lines`,
        { credentials: 'include' }
      );
      const data = await response.json();
      setLines(prev => ({ ...prev, [invoiceId]: data.lines || [] }));
    } catch (error) {
      console.error('Failed to fetch invoice lines:', error);
      setLines(prev => ({ ...prev, [invoiceId]: [] }));
    } finally {
      setLoadingLines(prev => ({ ...prev, [invoiceId]: false }));
    }
  };
  
  const availableColumns = {
    checkbox: { key: 'checkbox', label: '', width: '30px', sortable: false },
    invoiceNo: { key: 'invoice_no', label: 'Inv #', sortable: true },
    accountNo: { key: 'account_no', label: 'Acc #', sortable: true, filterable: true },
    /* "Customer", not "Name" — the column shows an account number and a
       company, and Name described neither well. sortFields makes its header
       open a choice instead of sorting one way: the cell carries two things
       people order by, and a plain click had to pick one. */
    account: {
      key: 'company_name', label: 'Customer', sortable: true, filterable: true,
      sortFields: [
        { value: 'account_no', label: 'Account number' },
        { value: 'company_name', label: 'Customer name' }
      ],
      sortLabels: { asc: 'A to Z / low to high', desc: 'Z to A / high to low' }
    },
    date: { key: 'invoice_date', label: 'Date', sortable: true, format: 'date' },
    dueDate: { key: 'due_date', label: 'Due', sortable: true, format: 'date' },
    reference: { key: 'reference', label: 'Ref', sortable: true },
    total: { key: 'total', label: 'Total', sortable: true, format: 'currency', align: 'left' },
    amount_paid: { key: 'amount_paid', label: 'Paid', sortable: true, format: 'currency', align: 'left' },
    balance: { key: 'balance', label: 'Balance', sortable: true, format: 'currency', align: 'left' },
    // filterable: the columns whose values repeat down the page, so a
    // right-click list is worth having. An invoice number or a total would give
    // a list where every option matches one row.
    status: { key: 'status', label: 'Status', sortable: true, format: 'status', align: 'center', filterable: true },
    approval: { key: 'approval_status', label: 'Approval', sortable: true, format: 'approval', align: 'center', filterable: true }
  };
  
  const visibleColumns = columns === 'all'
    ? Object.values(availableColumns)
    : columns.map(col => availableColumns[col]).filter(Boolean);

  /**
   * Right-click a header to narrow by the values on this page.
   *
   * Built from the column config rather than hard-coded, so every caller of
   * this table — invoices, statements, credit notes — gets it from marking a
   * column `filterable` and nothing else.
   */
  const filterDefinitions = useMemo(() => {
    const out = {};
    for (const column of visibleColumns) {
      if (!column.filterable) continue;
      out[column.key] = { label: column.label, value: (row) => row[column.key] };
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns]);

  /**
   * Server mode when the caller supplies facets, page-scoped otherwise.
   *
   * This table is shared — statements and credit-note screens render it too —
   * and only the ones whose endpoint computes facets can filter the whole
   * result set. The others keep the page-scoped behaviour rather than showing
   * an empty menu, and the menu states which it is.
   */
  // A rep's phone: one card per invoice instead of the table (1.14.0).
  const repMobile = useRepMobile();

  const cols = useColumnFilters(invoices, filterDefinitions, {
    facets,
    storageKey: columnFilterKey,
    onChange: onColumnFiltersChange
  });

  // The parent owns the search, so it needs the selection to send with it. Sent
  // up on change rather than read back out, so the table stays the only thing
  // that knows how a column filter is shaped.
  useEffect(() => {
    if (onColumnFiltersQuery) onColumnFiltersQuery(cols.queryValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cols.queryValue]);

  // Skipped on mount: a zero signal is "never asked", not "clear now", and
  // clearing on mount would wipe a selection the moment the page reloaded.
  const firstClearSignal = useRef(true);
  useEffect(() => {
    if (firstClearSignal.current) { firstClearSignal.current = false; return; }
    cols.clearEverything();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearColumnFiltersSignal]);

  // Drag-to-resize columns. Header order is:
  //   [expand cell?] + visibleColumns + [actions cell?]
  // so the rendered column count must include those fixed leading/trailing
  // cells alongside the prop-driven visible columns.
  const hasActions = actions.length > 0;

  // The row's action set, in RowActionsMenu/TableContextMenu shape. Shared by the
  // actions-column menu and the right-click menu so the two can't drift.
  const buildRowActions = (invoice) => actions
    .filter(a => a.type !== 'checkbox')
    .filter(action => !(action.isVisible && !action.isVisible(invoice)))
    .map(action => {
      const disabled = action.isDisabled?.(invoice);
      return {
        key: action.type,
        icon: action.icon,
        label: action.label,
        disabled,
        // disabledTitle may be a function of the row, for a reason per row.
        title: disabled
          ? ((typeof action.disabledTitle === 'function' ? action.disabledTitle(invoice) : action.disabledTitle) || action.label)
          : ((typeof action.title === 'function' ? action.title(invoice) : action.title) || action.label),
        className: action.className === 'btn-danger' ? 'action-danger' : action.className,
        onClick: () => action.handler(invoice)
      };
    });

  // Selection is read-only here and opt-in: consumers that track selected ids get
  // bulk right-click actions, the rest still get row + generic ones.
  const ctxMenu = useTableContextMenu({
    rows: invoices,
    selectedIds,
    getRowId: (inv) => inv.id
  });
  const leadingCols = expandable ? 1 : 0;
  const columnCount = useMemo(
    () => leadingCols + visibleColumns.length + (hasActions ? 1 : 0),
    [leadingCols, visibleColumns.length, hasActions]
  );
  const { tableRef, ResizeHandle, ColGroup, ResetButton } = useResizableColumns('searchInvoices', columnCount);

  const formatValue = (value, format) => {
    if (value === null || value === undefined) return '-';
    
    switch(format) {
      case 'date':
        return value ? new Date(value).toLocaleDateString('en-GB') : '-';
      case 'currency':
        return new Intl.NumberFormat('en-GB', {
          style: 'currency',
          currency: 'GBP',
          minimumFractionDigits: 2
        }).format(value || 0);
      case 'status':
        return (
          <span className={`status-badge ${value?.toLowerCase()}`}>
            {value}
          </span>
        );
      case 'approval':
        const approvalClass = {
          'approved': 'approved',
          'unapproved': 'unapproved', 
          'unchecked': 'unchecked'
        }[value] || 'unchecked';
        return (
          <span className={`approval-badge ${approvalClass}`}>
            {value || 'unchecked'}
          </span>
        );
      default:
        return value || '-';
    }
  };
  
  const handleSort = (column) => {
    if (!column.sortable || !onSort) return;
    onSort(column.key);
  };

  // Headers whose sort is a choice rather than a toggle open a popover; the
  // rest keep the plain click.
  const sortMenu = useSortMenu();
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  // Calculate total columns for colspan
  const totalColumns = visibleColumns.length + (actions.length > 0 ? 1 : 0) + (expandable ? 1 : 0);

  return (
    <>
      <div className="table-toolbar invoice-table-toolbar">
        <div className="table-toolbar-left">
          {toolbarLeft}
          <ResetButton />
        </div>
        {/* Between the reset button and the pagination, not under the table.
            Below the toolbar it sat beneath every control it relates to and
            read as part of the results rather than as a filter on them. */}
        <ColumnFilterNote cols={cols} header={headerFilters} onClear={onClearFilters} />
        {pagination && pagination.total > 0 && (
          <PaginationControls
            pagination={pagination}
            onPageChange={onPageChange}
          />
        )}
      </div>
      <div className="data-table-container">
        {repMobile ? (
          <RepDocCards loading={loading} count={cols.rows.length} empty={emptyMessage}>
            {cols.rows.map(invoice => {
              const card = repInvoiceCard(invoice);
              const preview = actions.find(a => a.type === 'preview');
              const open = preview ? () => preview.handler(invoice) : (onInvoiceClick ? () => onInvoiceClick(invoice) : null);
              return (
                <RepDocCard
                  key={invoice.id}
                  number={card.number}
                  kind={card.kind === 'Credit note' ? card.kind : ''}
                  title={card.title}
                  place={card.place}
                  when={card.when}
                  chip={card.chip}
                  total={card.total}
                  totalSub={card.balance}
                  archived={card.archived}
                  onOpen={open || undefined}
                  openLabel={`Open ${card.kind.toLowerCase()} ${card.number} for ${card.title}`}
                  actions={[{
                    key: 'customer',
                    label: 'Customer',
                    icon: 'person',
                    onClick: onContactClick && invoice.account_id ? () => onContactClick(invoice.account_id) : null,
                    disabledTitle: 'No customer account on this invoice'
                  }]}
                />
              );
            })}
          </RepDocCards>
        ) : loading ? (
          <LoadingSpinner />
        ) : invoices.length === 0 ? (
          <div className="table-empty">{emptyMessage}</div>
        ) : (
          <table className="data-table invoice-table resizable-table table-min-lg" ref={tableRef} onContextMenu={ctxMenu.onTableContextMenu}>
            <ColGroup />
            <thead>
              <tr>
                {expandable && <th className="expand-col"><ResizeHandle colIndex={0} /></th>}
                {visibleColumns.map((column, ci) => (
                  <th
                    key={column.key}
                    className={`${column.align || ''} ${column.sortable ? 'sortable' : ''} ${column.key === 'status' ? 'status-col' : ''} ${cols.isFiltered(column.key) ? 'col-filtered' : ''}`}
                    onClick={(e) => {
                      if (column.key === 'checkbox') return;
                      if (column.sortFields) return sortMenu.headerProps(column.key).onClick(e);
                      handleSort(column);
                    }}
                    onContextMenu={column.filterable ? cols.headerProps(column.key).onContextMenu : undefined}
                    title={column.filterable
                      ? `Right-click to filter by the ${column.label.toLowerCase()} values on this page`
                      : undefined}
                  >
                    {column.key === 'checkbox' ? (
                      <label className="tick-check-label" style={{ justifyContent: 'center', gap: '0' }}>
                        <input
                          type="checkbox"
                          className="tick-check-input"
                          id="select-all-invoices"
                          checked={actions.find(a => a.type === 'checkbox')?.allChecked || false}
                          onChange={() => actions.find(a => a.type === 'checkbox')?.headerHandler?.()}
                        />
                        <label htmlFor="select-all-invoices" className="tick-check">
                          <svg width="16px" height="16px" viewBox="0 0 18 18">
                            <path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z"></path>
                            <polyline points="1 9 7 14 15 4"></polyline>
                          </svg>
                        </label>
                      </label>
                    ) : (
                      <>
                        {column.label}{' '}
                        {column.sortable
                          && (column.sortFields
                            ? column.sortFields.some((f) => f.value === sortField)
                            : sortField === column.key)
                          && (sortDirection === 'asc' ? '▲' : '▼')}
                      </>
                    )}
                    <ResizeHandle colIndex={leadingCols + ci} />
                  </th>
                ))}
                {hasActions && <th className="actions-col"><ResizeHandle colIndex={leadingCols + visibleColumns.length} /></th>}
              </tr>
            </thead>
            <tbody>
              {cols.rows.map(invoice => (
                <React.Fragment key={invoice.id}>
                  <tr
                    className={expandable ? 'expandable-row' : ''}
                    onClick={expandable ? () => toggleRowExpand(invoice.id) : undefined}
                    onContextMenu={ctxMenu.onRowContextMenu(invoice)}
                  >
                    {expandable && (
                      <td className="expand-cell">
                        <span className={`expand-icon ${expanded.has(invoice.id) ? 'expanded' : ''}`}>
                          ▼
                        </span>
                      </td>
                    )}
                    {/* data-label is read by RepMobile.css to label the cell
                        when this row renders as a card on a rep's phone. Taken
                        from the column definition, so a column added later is
                        labelled for free. The checkbox has nothing to label and
                        the customer name is the card's title. */}
                    {visibleColumns.map(column => (
                      <td
                        key={column.key}
                        className={`${column.align || ''} ${column.key === 'status' ? 'status-col' : ''}`}
                        data-label={
                          column.key === 'checkbox' || column.key === 'company_name'
                            ? undefined
                            : column.label
                        }
                      >
                        {column.key === 'checkbox' ? (() => {
                          const cbAction = actions.find(a => a.type === 'checkbox');
                          const dragActive = !!cbAction?.onMouseDown;
                          return (
                            <label
                              className="tick-check-label"
                              style={{ justifyContent: 'center', gap: '0' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                // When drag-select is wired up, the toggle is applied during
                                // mousedown; suppress the label's default click-to-input so
                                // onChange doesn't double-toggle.
                                if (dragActive) e.preventDefault();
                              }}
                              onMouseDown={cbAction?.onMouseDown ? cbAction.onMouseDown(invoice) : undefined}
                              onMouseEnter={cbAction?.onMouseEnter ? cbAction.onMouseEnter(invoice) : undefined}
                            >
                              <input
                                type="checkbox"
                                className="tick-check-input"
                                id={`select-inv-${invoice.id}`}
                                checked={invoice.checked || false}
                                onChange={() => cbAction?.handler(invoice)}
                              />
                              <label htmlFor={`select-inv-${invoice.id}`} className="tick-check">
                                <svg width="16px" height="16px" viewBox="0 0 18 18">
                                  <path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z"></path>
                                  <polyline points="1 9 7 14 15 4"></polyline>
                                </svg>
                              </label>
                            </label>
                          );
                        })() : column.key === 'invoice_no' && onInvoiceClick ? (
                          <span
                            className="invoice-number-link"
                            onClick={(e) => { e.stopPropagation(); onInvoiceClick(invoice); }}
                            style={{ cursor: 'pointer', fontWeight: 'bold' }}
                          >
                            {invoice.invoice_no}
                          </span>
                        ) : column.key === 'invoice_no' ? (
                          // No consumer-supplied handler \u2014 fall back to the shared
                          // entity modal so the number is still clickable.
                          <EntityLink
                            type={invoice.is_credit ? 'credit_note' : 'invoice'}
                            id={invoice.id}
                            className="invoice-number-link"
                            style={{ fontWeight: 'bold' }}
                          >
                            {invoice.invoice_no}
                          </EntityLink>
                        ) : column.key === 'company_name' ? (
                          <span className="name-with-badge">
                            <span
                              className={`account-badge-inline${!invoice.account_no ? ' empty' : ''}`}
                              style={(onContactClick && invoice.account_id && invoice.account_no) ? { cursor: 'pointer' } : undefined}
                              onClick={(onContactClick && invoice.account_id && invoice.account_no) ? (e) => { e.stopPropagation(); onContactClick(invoice.account_id); } : undefined}
                            >
                              {onContactClick || !invoice.account_no ? (
                                invoice.account_no || '\u00A0'
                              ) : (
                                <EntityLink type="contact" id={invoice.account_id} variant="badge">
                                  {invoice.account_no}
                                </EntityLink>
                              )}
                            </span>
                            {onContactClick ? (
                              <span
                                className="contact-link"
                                onClick={(e) => { e.stopPropagation(); onContactClick(invoice.account_id); }}
                                style={{ cursor: 'pointer' }}
                              >
                                {formatValue(invoice[column.key], column.format)}
                              </span>
                            ) : (
                              <EntityLink type="contact" id={invoice.account_id} className="contact-link">
                                {formatValue(invoice[column.key], column.format)}
                              </EntityLink>
                            )}
                            {Array.isArray(invoice.customer_changes) && invoice.customer_changes.length > 0 && (
                              <span
                                className="material-symbols-rounded customer-changed-icon"
                                title={describeCustomerChanges(invoice.customer_changes)}
                                aria-label="Customer changed"
                              >swap_horiz</span>
                            )}
                          </span>
                        ) : column.format === 'status' ? (
                          <>
                            {formatValue(invoice[column.key], column.format)}
                            {!invoice.xero_invoice_id && (
                              <span className="material-symbols-rounded broken-link-icon" title="Not linked to Xero">link_off</span>
                            )}
                            {/* 1.15.0: units corrected on its order after it was paid. */}
                            <InvoiceCorrectionBadge flags={invoice.correction_flags} />
                          </>
                        ) : (
                          formatValue(invoice[column.key], column.format)
                        )}
                      </td>
                    ))}
                    {actions.length > 0 && (
                      <td className="actions-col" onClick={(e) => e.stopPropagation()}>
                        <RowActionsMenu actions={buildRowActions(invoice)} />
                      </td>
                    )}
                  </tr>
                  {expandable && (
                    <tr className={`expanded-content ${expanded.has(invoice.id) ? 'expanded' : ''}`}>
                      <td colSpan={totalColumns}>
                        <div className="line-items-container">
                          {loadingLines[invoice.id] ? (
                            <div className="loading-lines">Loading line items...</div>
                          ) : lines[invoice.id]?.length > 0 ? (
                            <table className="line-items-table">
                              <thead>
                                <tr>
                                  <th>Sales description</th>
                                  <th className="text-right">Qty</th>
                                  <th className="text-right">Unit</th>
                                  <th className="text-right">Discount</th>
                                  <th className="text-right">Line</th>
                                  <th className="text-right">VAT</th>
                                </tr>
                              </thead>
                              <tbody>
                                {lines[invoice.id].map((line, idx) => (
                                  <tr key={idx}>
                                    {/* The item code as a badge left of the words (Cam, 21 Oct 2026). */}
                                    <td><ItemCodeDescription code={line.item_code} itemId={line.item_id} description={line.description} /></td>
                                    <td className="text-right"><QtyWithTonnes quantity={line.quantity} line={line} /></td>
                                    <td className="text-right">{formatPrice(line.unit_price || 0)}</td>
                                    <td className="text-right">{parseFloat(line.discount_amount || 0) > 0 ? formatCurrency(line.discount_amount) : ''}</td>
                                    <td className="text-right">{formatCurrency(line.line_total)}</td>
                                    <td className="text-right">{formatVatWithRate(line.tax_amount, line.tax_type, formatCurrency)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : expanded.has(invoice.id) && !loadingLines[invoice.id] ? (
                            <div className="no-lines">No line items found</div>
                          ) : null}
                          {showSettlement && expanded.has(invoice.id) && settlement[invoice.id] &&
                            (settlement[invoice.id].payments.length > 0 || settlement[invoice.id].creditAllocations.length > 0
                              || (settlement[invoice.id].adjustments || []).length > 0) && (
                            <div className="settlement-strip">
                              {(settlement[invoice.id].adjustments || []).map((a, ai) => {
                                const word = a.kind === 'credit' ? 'Credit' : 'Debit';
                                const label = a.role === 'for' ? `${word} for` : word;
                                return (
                                  <span key={`a${ai}`} className="settlement-pill adjustment"
                                    title={a.role === 'for' ? `Raised for ${a.invoice_no}` : `${word} raised for this invoice: ${a.invoice_no}`}>
                                    <span className="material-symbols-rounded">link</span>
                                    <em>{label}</em>
                                    <EntityLink type={a.is_credit ? 'credit_note' : 'invoice'} id={a.id} variant="badge">
                                      {a.invoice_no}
                                    </EntityLink>
                                    <strong>{formatCurrency(a.total)}</strong>
                                  </span>
                                );
                              })}
                              {settlement[invoice.id].creditAllocations.map((c, ci) => (
                                <span key={`c${ci}`} className="settlement-pill credit" title={`${c.type === 'credit_applied' ? 'Credit note applied' : 'Allocated to'} ${c.linkedNo}`}>
                                  <span className="material-symbols-rounded">credit_score</span>
                                  <EntityLink
                                    type={c.type === 'credit_applied' ? 'credit_note' : 'invoice'}
                                    id={c.linkedId}
                                    variant="badge"
                                  >
                                    {c.linkedNo}
                                  </EntityLink>
                                  {c.date && <em>{formatDateShort(c.date)}</em>}
                                  <strong>{formatCurrency(c.amount)}</strong>
                                </span>
                              ))}
                              {settlement[invoice.id].payments.map((p, pi) => (
                                <span key={`p${pi}`} className="settlement-pill payment" title={paymentNature(p)}>
                                  <span className="material-symbols-rounded">payments</span>
                                  <EntityLink type="payment" id={p.id} variant="badge">
                                    {p.bank_account_name || 'Payment'}
                                  </EntityLink>
                                  {p.payment_date && <em>{formatDateShort(p.payment_date)}</em>}
                                  <strong>{formatCurrency(p.amount)}</strong>
                                </span>
                              ))}
                            </div>
                          )}
                          {invoice.notes && expanded.has(invoice.id) && (
                            <div className="line-items-notes" style={{ padding: '4px 12px 8px', fontSize: 'var(--font-xs)', color: 'var(--text-muted)' }}>
                              <span className="material-symbols-rounded" style={{ fontSize: 12, verticalAlign: 'middle' }}>notes</span> {invoice.notes}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Outside the hasActions gate: a read-only invoice table still wants its
          column filters, and the row-action menu is what hasActions governs. */}
      <ColumnFilterMenu cols={cols} />

      {/* One per column that declares a sort choice — only Customer, today. */}
      {visibleColumns.filter((c) => c.sortFields).map((column) => (
        <SortOptionsMenu
          key={column.key}
          menu={sortMenu}
          menuKey={column.key}
          title={`Sort by ${column.label.toLowerCase()}`}
          fields={column.sortFields}
          field={sortField}
          direction={sortDirection}
          labels={column.sortLabels}
          onChange={(field, direction) => onSort?.(field, direction)}
        />
      ))}

      {hasActions && (
        <TableContextMenu
          ctx={ctxMenu}
          rowActions={buildRowActions}
          bulkActions={bulkActions || undefined}
          actions={genericActions || []}
        />
      )}
    </>
  );
}

export default InvoiceTable;